import React, { useState, useEffect } from "react";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { getMint, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import {
  Coins,
  Play,
  Pause,
  Activity,
  Shield,
  AlertTriangle,
} from "lucide-react";
import TokenActions from "./TokenActions";
import { AdminAirdropManagement } from "./AdminAirdropManagement";
import AuthorityManagement from "./AuthorityManagement";

const AdminDashboard = ({ mintAddress }) => {
  const { publicKey, connected } = useWallet();
  const { connection } = useConnection();
  const { toast } = useToast();

  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const refreshTokenInfo = async () => {
    if (!mintAddress || !connection) return;

    setLoading(true);
    try {
      const mintPubkey = new PublicKey(mintAddress);
      // Use Token Extensions program ID
      const TOKEN_EXTENSIONS_PROGRAM_ID = TOKEN_PROGRAM_ID;
      const mintInfo = await getMint(
        connection,
        mintPubkey,
        "confirmed",
        TOKEN_EXTENSIONS_PROGRAM_ID
      );

      setTokenInfo({
        supply: Number(mintInfo.supply) / Math.pow(10, mintInfo.decimals),
        decimals: mintInfo.decimals,
        mintAuthority: mintInfo.mintAuthority?.toString() || null,
        freezeAuthority: mintInfo.freezeAuthority?.toString() || null,
        isInitialized: mintInfo.isInitialized,
      });

      // Check if current wallet is admin
      if (publicKey && mintInfo.mintAuthority) {
        setIsAdmin(publicKey.toString() === mintInfo.mintAuthority.toString());
      }
    } catch (error) {
      console.error("Error loading token info:", error);
      toast({
        title: "Error loading token",
        description: "Failed to load token information",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!mintAddress || !connection || !connected) return;

      setLoading(true);
      try {
        const mintPubkey = new PublicKey(mintAddress);
        // Use Token Extensions program ID
        const TOKEN_EXTENSIONS_PROGRAM_ID = TOKEN_PROGRAM_ID;
        const mintInfo = await getMint(
          connection,
          mintPubkey,
          "confirmed",
          TOKEN_EXTENSIONS_PROGRAM_ID
        );

        setTokenInfo({
          supply: Number(mintInfo.supply) / Math.pow(10, mintInfo.decimals),
          decimals: mintInfo.decimals,
          mintAuthority: mintInfo.mintAuthority?.toString() || null,
          freezeAuthority: mintInfo.freezeAuthority?.toString() || null,
          isInitialized: mintInfo.isInitialized,
        });
        // Check if current wallet is admin
        if (publicKey && mintInfo.mintAuthority) {
          setIsAdmin(
            publicKey.toString() === mintInfo.mintAuthority.toString()
          );
        }
      } catch (error) {
        console.error("Error loading token info:", error);
        toast({
          title: "Error loading token",
          description: "Failed to load token information",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [connected, mintAddress, publicKey, connection, toast]);

  if (!connected) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-16"
      >
        <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-2xl font-bold mb-2">Admin Access Required</h2>
        <p className="text-muted-foreground mb-6">
          Connect your wallet to access the admin dashboard
        </p>
      </motion.div>
    );
  }

  if (!isAdmin && tokenInfo) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-16"
      >
        <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-warning" />
        <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
        <p className="text-muted-foreground mb-6">
          This wallet does not have admin privileges for this token
        </p>
        <Badge variant="outline" className="font-mono text-xs">
          Admin: {tokenInfo.mintAuthority?.slice(0, 8)}...
          {tokenInfo.mintAuthority?.slice(-8)}
        </Badge>
      </motion.div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-tab-main-wrapper">
      <div className="container">
        <div className="space-y-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-2">
              🛡️ Admin Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage your SPL token with advanced controls
            </p>
          </motion.div>

          {/* Token Stats */}
          {tokenInfo && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="section_03 total-stats-section">
                <div className="row mb-8">
                  <div className="col-md-3">
                    <Card className="bg-gradient-card card_01 ">
                      <CardHeader className="d-flex flex-row align-items-center justify-content-between small-text space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                          Total Supply
                        </CardTitle>
                        <Coins className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-primary">
                          {tokenInfo.supply.toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {tokenInfo.decimals} decimals
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="col-md-3">
                    <Card className="bg-gradient-card card_01 ">
                      <CardHeader className="d-flex flex-row align-items-center justify-content-between small-text space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                          Status
                        </CardTitle>
                        <Activity className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-success">
                          {tokenInfo.isInitialized ? "Active" : "Inactive"}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Token status
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="col-md-3">
                    <Card className="bg-gradient-card card_01 ">
                      <CardHeader className="d-flex flex-row align-items-center justify-content-between small-text space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                          Authority
                        </CardTitle>
                        <Shield className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-accent">
                          Admin
                        </div>
                        <p className="text-xs text-muted-foreground font-mono">
                          {publicKey?.toString().slice(0, 8)}...
                          {publicKey?.toString().slice(-8)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="col-md-3">
                    <Card className="bg-gradient-card card_01 ">
                      <CardHeader className="d-flex flex-row align-items-center justify-content-between small-text space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                          Token Status
                        </CardTitle>
                        {tokenInfo?.isPaused === true ? (
                          <Pause className="h-4 w-4 text-warning" />
                        ) : tokenInfo?.isPaused === false ? (
                          <Play className="h-4 w-4 text-success" />
                        ) : (
                          <Activity className="h-4 w-4 text-muted-foreground" />
                        )}
                      </CardHeader>
                      <CardContent>
                        <div
                          className={`text-2xl font-bold ${
                            tokenInfo?.isPaused === true
                              ? "text-warning"
                              : tokenInfo?.isPaused === false
                              ? "text-success"
                              : "text-muted-foreground"
                          }`}
                        >
                          {tokenInfo?.isPaused === true
                            ? "Paused"
                            : tokenInfo?.isPaused === false
                            ? "Active"
                            : "Unknown"}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {tokenInfo?.isPaused === true
                            ? "Mint, burn & transfer disabled"
                            : tokenInfo?.isPaused === false
                            ? "All operations enabled"
                            : "Status unknown"}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Token Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <TokenActions
              mintAddress={mintAddress}
              onActionComplete={refreshTokenInfo}
              isAdmin={isAdmin}
            />
          </motion.div>

          {/* Authority Management */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <AuthorityManagement
              mintAddress={mintAddress}
              onAuthorityChanged={refreshTokenInfo}
              isAdmin={isAdmin}
            />
          </motion.div>

          {/* Airdrop Management */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <AdminAirdropManagement />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
